import { withRouter } from 'react-router-dom';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import moment from 'moment-timezone';
import { CANCELLED_STATUSES, TERMINAL_STATUSES, IN_PROGRESS_STATUSES } from "../../constants/statuses";
import { getLocalItem } from '../../utils/storage';

class MechanicBookingDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mechanicName: '',
      mechanicPhone: '',
      bookingId: null,
      booking: null,
      loading: true,
      error: '',
      
      // For UI interactions
      selectedBookingNotes: '',
      notesSuccessMessage: '',
      selectedPhotos: [],
      uploadSuccessMessage: '',
      submitNotesDisabled: false,
      
      // OTP
      isSendingOtp: false,
      isOtpSent: false,
      enteredOtp: '',
      otpVerificationMessage: '',
      isOtpVerified: false,
      
      // Vendor
      nearbyVendors: [],
      selectedVendor: null,
      porterBookingMessage: '',
      isBookingPorter: false,
      
      // Worker
      selectedWorker: '',
      workerDetails: [],
      
      // Vehicle data
      vehicleMakesCar: [],
      vehicleMakesBike: [],
      vehicleModels: {},
      vehicleYears: [],
      
      // Parts & Invoice
      selectedPartsList: [],
      commonParts: '',
      allParts: '',
      invoiceGenerated: false,
      invoiceMessage: '',
      disableInvoiceButton: false,
    };
  }

  async componentDidMount() {
    this.props.setShowChatBot(false);
    
    // Check authentication
    const token = getLocalItem('authToken');
    const mechanicPhone = getLocalItem('mechanic_phone');
    
    if (!token || !mechanicPhone) {
      // Not authenticated, redirect to login
      this.props.history.push('/mechanic-login');
      return;
    }
    
    // Get booking ID from URL
    const bookingId = this.props.match.params.bookingId;
    
    if (!bookingId) {
      this.setState({ error: 'Invalid booking ID', loading: false });
      return;
    }
    
    // Verify authentication
    try {
      const authResponse = await axios.get('https://ridenrepair.com/api/get_user_data', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = authResponse.data.user_data;
      
      this.setState({
        mechanicName: userData.mechanic_name,
        mechanicPhone: userData.mechanic_phone,
        bookingId: parseInt(bookingId)
      });
      
      // Fetch booking details
      await this.fetchBookingDetails(bookingId);
      await this.fetchVehicleMasterData();
      await this.fetchWorkers(userData.mechanic_phone);
      await this.fetchNearbyVendors(userData.mechanic_phone);
      await this.fetchPartsData();
      
    } catch (error) {
      // Authentication failed, redirect to login
      this.props.history.push('/mechanic-login');
    }
  }

  fetchBookingDetails = async (bookingId) => {
    try {
      const response = await axios.get('https://ridenrepair.com/api/get_booking_via_actual_booking_id', {
        params: { booking_id: bookingId, fetch_phone: true }
      });
      
      if (response.data) {
        const booking = response.data;
        const { mechanicPhone } = this.state;
        
        // CRITICAL: Verify this booking belongs to the logged-in mechanic
        // If booking has no mechanic_number OR it doesn't match logged-in mechanic, redirect
        if (!booking.mechanic_number || booking.mechanic_number.toString() !== mechanicPhone.toString()) {
          // Booking doesn't belong to this mechanic or has no mechanic assigned - redirect to login
          this.props.history.push('/mechanic-login');
          return;
        }
        
        this.setState({ 
          booking: booking,
          selectedBookingNotes: booking.mechanic_notes || '',
          loading: false 
        });
      } else {
        this.setState({ error: 'Booking not found', loading: false });
      }
    } catch (error) {
      // If booking fetch fails, redirect to login for security
      this.props.history.push('/mechanic-login');
    }
  };

  fetchVehicleMasterData = async () => {
    try {
      const [makesCarRes, makesBikeRes, yearsRes] = await Promise.all([
        axios.get('https://ridenrepair.com/auto/vehicle_makes', { params: { type: 'car' } }),
        axios.get('https://ridenrepair.com/auto/vehicle_makes', { params: { type: 'bike' } }),
        axios.get('https://ridenrepair.com/auto/vehicle_years')
      ]);
      
      this.setState({
        vehicleMakesCar: makesCarRes.data.makes || [],
        vehicleMakesBike: makesBikeRes.data.makes || [],
        vehicleYears: yearsRes.data.years || []
      });
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
    }
  };

  fetchWorkers = async (mechanicPhone) => {
    try {
      const response = await axios.get(`https://ridenrepair.com/auto/garage_workers?mechanic_phone=${mechanicPhone}`);
      this.setState({ workerDetails: response.data.workers || [] });
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  fetchNearbyVendors = async (mechanicPhone) => {
    try {
      const response = await axios.get('https://ridenrepair.com/auto/get_nearby_spare_vendors', {
        params: { mechanic_phone: mechanicPhone }
      });
      this.setState({ nearbyVendors: response.data || [] });
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  goBack = () => {
    this.props.history.push('/mechanic-login');
  };

  openGoogleMaps = (latitude, longitude) => {
    // Since this is React Native Android only, use postMessage to communicate with native code
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    const appUrl = `google.navigation:q=${latitude},${longitude}`;
    
    // Method 1: Use React Native WebView postMessage to communicate with native Android code
    // The native code should handle opening Google Maps app or browser
    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'OPEN_GOOGLE_MAPS',
        latitude: latitude,
        longitude: longitude,
        appUrl: appUrl,
        webUrl: webUrl
      }));
      return;
    }
    
    // Fallback Method 2: Try to open Google Maps app directly
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.src = appUrl;
      document.body.appendChild(iframe);
      
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch (e) {
          // Iframe might have been removed already
        }
      }, 1000);
      
      // Fallback: If app doesn't open, try to open in system browser
      setTimeout(() => {
        // Try to break out of WebView using location.href
        window.location.href = webUrl;
      }, 2000);
    } catch (e) {
      // If all else fails, navigate to web URL
      window.location.href = webUrl;
    }
  };

  sendOtpToCustomer = async () => {
    this.setState({ isSendingOtp: true, otpVerificationMessage: '' });
    try {
      const { booking } = this.state;
      
      // Validate required fields
      if (!booking || !booking.phone || !booking.booking_source) {
        this.setState({
          otpVerificationMessage: 'Missing booking information. Please try again.',
          isSendingOtp: false
        });
        return;
      }

      const response = await axios.post('https://www.ridenrepair.com/api/send_start_service_otp_sms', {
        phone: booking.phone,
        booking_source: booking.booking_source
      });

      if (response.status === 200) {
        this.setState({ isOtpSent: true, otpVerificationMessage: 'OTP sent to customer successfully.' });
      } else {
        this.setState({ 
          otpVerificationMessage: 'Failed to send OTP. Please try again.',
          isSendingOtp: false
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error sending OTP. Please try again.';
      this.setState({ 
        otpVerificationMessage: `Error: ${errorMessage}`,
        isSendingOtp: false
      });
    }
  };

  verifyOtp = async () => {
    const { enteredOtp, booking, mechanicPhone } = this.state;
    try {
      // Mechanic-only OTP-verify endpoint. Validates OTP, advances booking
      // to SERVICE STARTED, and fires the service_started Temporal event
      // tagged actor_type='mechanic' on the server side. Replaces the older
      // chained validate_booking_otp + updateBookingStatus pattern that
      // tagged events as actor_type='ops'.
      const response = await axios.post('https://www.ridenrepair.com/api/validate_service_start_otp', {
        booking_id: booking.booking_id,
        email: booking.email,
        phone: booking.phone,
        booking_otp: enteredOtp,
        mechanic_phone: mechanicPhone,
      });
      if (response.status === 200 && response.data.is_valid) {
        this.setState({
          isOtpVerified: true,
          otpVerificationMessage: 'OTP verified successfully.',
          booking: { ...booking, order_status: 'SERVICE STARTED' },
        });
        // No client-side updateBookingStatus chain — the server already
        // wrote SERVICE STARTED + fired the Temporal event in one place.
      } else {
        this.setState({ otpVerificationMessage: 'Invalid OTP. Please try again.' });
      }
    } catch (error) {
      this.setState({ otpVerificationMessage: 'Error verifying OTP. Please try again.' });
    }
  };

  callCustomer = () => {
    try {
      const { booking, mechanicPhone } = this.state;
      const params = {
        mechanic_number: mechanicPhone,
        phone: booking.phone,
        booking_id: booking.booking_id
      };
      axios.post(`https://ridenrepair.com/api/sip_connect_mechanic_to_customer`, params);
    } catch (error) {
    }
  };

  updateBookingStatus = (order_status) => {
    const { booking, mechanicName, mechanicPhone } = this.state;
    // actor_type/actor_id let the server tag the resulting Temporal event as
    // mechanic-driven. Without these, server.js defaults to actor_type='ops'.
    const params = {
      booking_id: booking.booking_id,
      email: booking.email,
      order_status: order_status,
      change_history: 'Status updated by ' + mechanicName,
      actor_type: 'mechanic',
      actor_id: mechanicPhone,
      mechanic_phone: mechanicPhone,
    };

    axios.post('https://ridenrepair.com/api/update_booking_status', params);
    axios.post('https://ridenrepair.com/api/update_booking_change_history', params);

    // Update local state
    this.setState({
      booking: { ...booking, order_status: order_status }
    });
  };

  handleNotesChange = (event) => {
    this.setState({ selectedBookingNotes: event.target.value });
  };

  submitNotesUpdate = () => {
    const { selectedBookingNotes, booking, mechanicName } = this.state;
    this.setState({ submitNotesDisabled: true });
    axios.post('https://ridenrepair.com/api/update_booking_mechanic_notes', {
      booking_id: booking.booking_id,
      email: booking.email,
      mechanic_notes: encodeURIComponent(selectedBookingNotes) + ' by ' + mechanicName
    })
    .then(() => this.setState({ notesSuccessMessage: 'Notes updated successfully!' }))
    .catch(() => this.setState({ notesSuccessMessage: 'Failed to update notes.' }))
    .finally(() => {
      setTimeout(() => this.setState({ submitNotesDisabled: false }), 3000);
    });
  };

  assignWorker = (selectedWorker) => {
    const { booking } = this.state;
    if (!booking) return;
    const params = {
      booking_id: booking.booking_id,
      email: booking.email,
      worker_name: selectedWorker.worker_name,
      worker_number: selectedWorker.worker_number
    };
    axios.post(`https://ridenrepair.com/api/update_booking_worker_mechanic`, params);
  };

  callVendor = () => {
    try {
      const { selectedVendor, mechanicPhone, booking } = this.state;
      const params = {
        mechanic_number: mechanicPhone,
        phone: selectedVendor.phone,
        booking_id: booking.booking_id,
      };
      axios.post('https://ridenrepair.com/api/sip_connect_mechanic_to_vendor', params);
    } catch (error) {
    }
  };

  bookPorter = async () => {
    this.setState({ isBookingPorter: true, porterBookingMessage: 'Booking Porter...' });
    try {
      const { selectedVendor, mechanicPhone, booking } = this.state;
      const params = {
        vendor_phone: selectedVendor.phone,
        mechanic_phone: mechanicPhone,
        booking_id: booking.booking_id
      };
      const response = await axios.post('https://ridenrepair.com/api/create_pickup_order', params);
      if (response.status === 201) {
        this.setState({ porterBookingMessage: 'Porter booked successfully!' });
      } else {
        this.setState({ porterBookingMessage: 'Failed to book porter.' });
      }
    } catch (error) {
      const status = error.response && error.response.status;
      this.setState({
        porterBookingMessage: status === 409 ? 'Porter already booked for this booking.' : 'Error booking porter.',
      });
    } finally {
      setTimeout(() => {
        this.setState({ isBookingPorter: false, porterBookingMessage: '' });
      }, 5000);
    }
  };

  handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    this.setState((prevState) => ({
      selectedPhotos: [...prevState.selectedPhotos, ...files],
    }));
  };

  uploadPhotos = async () => {
    const { booking, selectedPhotos } = this.state;

    // iOS app: WKWebView cannot perform multipart uploads, so route through the
    // native bridge. This flag is set ONLY by the iOS app — web and Android fall
    // through to the unchanged axios upload below.
    if (typeof window !== 'undefined' && window.__rnrIOSNativeUpload && window.ReactNativeWebView) {
      try {
        const toB64 = (file) => new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(',')[1] || '');
          r.onerror = () => rej(new Error('read failed'));
          r.readAsDataURL(file);
        });
        const photos = [];
        for (const f of selectedPhotos) {
          photos.push({ name: f.name || 'photo.jpg', type: f.type || 'image/jpeg', data: await toB64(f) });
        }
        window.__rnrBookingPhotoResult = (success, message) => {
          this.setState({ uploadSuccessMessage: message || (success ? 'Photos uploaded successfully!' : 'Failed to upload photos.') });
        };
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'NATIVE_BOOKING_PHOTO_UPLOAD',
          booking_id: booking.booking_id,
          email: booking.email,
          photos,
        }));
      } catch (e) {
        this.setState({ uploadSuccessMessage: 'Error uploading photos.' });
      }
      return;
    }

    const formData = new FormData();
    formData.append('booking_id', booking.booking_id);
    formData.append('email', booking.email);
    selectedPhotos.forEach((file) => {
      formData.append('photos', file);
    });
    try {
      const response = await axios.post('https://www.ridenrepair.com/api/upload_booking_photo', formData);
      if (response.status === 200) {
        this.setState({ uploadSuccessMessage: 'Photos uploaded successfully!' });
      } else {
        this.setState({ uploadSuccessMessage: 'Failed to upload photos.' });
      }
    } catch (error) {
      this.setState({ uploadSuccessMessage: 'Error uploading photos.' });
    }
  };

  handleVehicleMakeChange = async (event) => {
    const make = event.target.value;
    const { booking } = this.state;
    this.setState({
      booking: { ...booking, vehicle_make: make, vehicle_model: '' }
    });
    await axios.post('https://ridenrepair.com/api/update_booking_vehicle_make', {
      booking_id: booking.booking_id,
      email: booking.email,
      vehicle_make: make
    });
  };

  handleVehicleModelChange = async (event) => {
    const model = event.target.value;
    const { booking } = this.state;
    this.setState({
      booking: { ...booking, vehicle_model: model }
    });
    await axios.post('https://ridenrepair.com/api/update_booking_vehicle_model', {
      booking_id: booking.booking_id,
      email: booking.email,
      vehicle_model: model
    });
  };

  handleVehicleYearChange = async (event) => {
    const year = event.target.value;
    const { booking } = this.state;
    this.setState({
      booking: { ...booking, vehicle_year: year }
    });
    await axios.post('https://ridenrepair.com/api/update_booking_vehicle_year', {
      booking_id: booking.booking_id,
      email: booking.email,
      vehicle_year: year
    });
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = date.getUTCFullYear();
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${month} ${day}, ${year} ${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  fetchPartsData = () => {
    fetch('https://ridenrepair.com/api/get_common_parts')
      .then((response) => response.json())
      .then((data) => {
        this.setState({ commonParts: data });
      })
      .catch((error) => {
      });
    
    fetch('https://ridenrepair.com/api/get_all_parts')
      .then((response) => response.json())
      .then((data) => {
        this.setState({ allParts: data });
      })
      .catch((error) => {
      });
  };

  handleClickPart = (part) => {
    const { selectedPartsList } = this.state;
    const newPart = {
      partId: part.part_id,
      partName: part.part_name,
      salesPrice: '',
      costPrice: '',
      quantity: 1
    };
    this.setState({
      selectedPartsList: [...selectedPartsList, newPart]
    });
  };

  handleAddOtherPart = () => {
    const { selectedPartsList } = this.state;
    const newPart = {
      partName: '',
      salesPrice: '',
      costPrice: '',
      quantity: 1
    };
    this.setState({
      selectedPartsList: [...selectedPartsList, newPart]
    });
  };

  handleSelectPart = (event) => {
    event.preventDefault();
    const selectedPartId = event.target.options[event.target.selectedIndex].getAttribute('data-part-id');
    const selectedPartName = event.target.options[event.target.selectedIndex].getAttribute('data-part-name');
    const { selectedPartsList } = this.state;
    const newPart = {
      partId: selectedPartId,
      partName: selectedPartName,
      salesPrice: '',
      costPrice: '',
      quantity: 1
    };
    this.setState({
      selectedPartsList: [...selectedPartsList, newPart]
    });
  };

  handleOtherPartNameChange = (index, value) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].partName = value;
    this.setState({
      selectedPartsList: updatedSelectedPartsList
    });
  };

  handleQuantityChange = (index, quantity) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].quantity = quantity;
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };

  handleSalesPriceChange = (index, salesPrice) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList[index].salesPrice = salesPrice;
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };

  removePart = (index) => {
    const { selectedPartsList } = this.state;
    const updatedSelectedPartsList = [...selectedPartsList];
    updatedSelectedPartsList.splice(index, 1);
    this.setState({ selectedPartsList: updatedSelectedPartsList });
  };

  loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  onSuccessfulPayment = async (paymentData) => {
    try {
      await axios.post('https://ridenrepair.com/api/mechanic_razorpay_success', paymentData);
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  displayRazorpay = async (amount) => {
    const res = await this.loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: amount * 100, currency: 'INR' })
    };

    const data = await fetch('https://ridenrepair.com/api/razorpay', requestOptions).then(
      (response) => response.json()
    );

    const options = {
      key: 'rzp_live_v9Y9ZhDUWFgjNI',
      amount: amount,
      currency: data.currency,
      name: 'Ride N Repair',
      description: 'Mechanic Payment',
      image: 'https://ridenrepair.com/api/logo.png',
      order_id: data.id,
      handler: (response) => { this.onSuccessfulPayment(data); },
      prefill: {
        name: this.state.mechanicName,
        contact: this.state.mechanicPhone,
      },
      theme: {
        color: "#61dafb",
      },
    };
    
    if (typeof window !== 'undefined') {
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    }
  };

  generateInvoice = async () => {
    this.setState({ invoiceMessage: 'Generating Invoice', disableInvoiceButton: true });
    if (!this.state.invoiceGenerated || this.state.disableInvoiceButton) {
      try {
        const { booking, selectedPartsList } = this.state;
        if (selectedPartsList.some((part) => !part.salesPrice)) {
          this.setState({ invoiceMessage: 'Please fill in all the Sales prices' });
          return;
        }
        const partsNames = selectedPartsList.map((part) => part.partName);
        const salesPrices = selectedPartsList.map((part) => part.salesPrice);

        let email = '';
        if (booking.secondary_email) {
          email = booking.secondary_email;
        } else {
          email = booking.email;
        }
        const { mechanicPhone } = this.state;
        // actor_type/actor_id tag the resulting payment_link_sent and
        // (downstream) invoice_generated Temporal events as mechanic-driven.
        // Server.js defaults to actor_type='ops' if these are absent.
        const params = {
          booking_id: booking.booking_id,
          phone: booking.phone,
          email: email,
          charges_desc: partsNames.join(','),
          amount_details: salesPrices.join(','),
          paid_amount: booking.paid_amount,
          actor_type: 'mechanic',
          actor_id: mechanicPhone,
        };

        const paymentLink = await axios.get("https://ridenrepair.com/api/get_rzp_pay_link", { params });

        const updatedBooking = {
          ...booking,
          payment_link: paymentLink.data.short_url,
          payment_link_id: paymentLink.data.payment_link_id,
          final_payment_amount: paymentLink.data.final_amount,
          invoice_id: paymentLink.data.invoice_id,
          charges_desc: params.charges_desc,
          amount_details: params.amount_details,
        };
        
        if (paymentLink.data.prepaid) {
          const mechanicparams = {
            phone: booking['mechanic_number'],
            status: 'AVAILABLE',
          }
          try {
            axios.post("https://ridenrepair.com/auto/mechanic_status", mechanicparams);
          } catch (error) {
          }
        } else {
          try {
            axios.post(`https://ridenrepair.com/api/update_booking_invoice`, updatedBooking);
          } catch (error) {
          }
          try {
            axios.post("https://ridenrepair.com/whatsapp/sendpaylink", updatedBooking);
          } catch (error) {
          }
          const mechanic_params = {
            phone: booking['mechanic_number'],
            status: 'AVAILABLE',
          }
          try {
            axios.post("https://ridenrepair.com/auto/mechanic_status", mechanic_params);
          } catch (error) {
          }
        }
        this.setState({ 
          booking: updatedBooking,
          invoiceMessage: 'Invoice Generated',
          invoiceGenerated: true
        });

        setTimeout(() => {
          this.setState({ disableInvoiceButton: false });
        }, 5000);
      } catch (error) {
        this.setState({ invoiceMessage: 'Error generating invoice' });
      }
    }
  };

  render() {
    const {
      loading,
      error,
      booking,
      mechanicName,
      selectedBookingNotes,
      notesSuccessMessage,
      selectedPhotos,
      uploadSuccessMessage,
      submitNotesDisabled,
      isSendingOtp,
      isOtpSent,
      enteredOtp,
      otpVerificationMessage,
      isOtpVerified,
      nearbyVendors,
      selectedVendor,
      porterBookingMessage,
      isBookingPorter,
      selectedWorker,
      workerDetails,
      vehicleMakesCar,
      vehicleMakesBike,
      vehicleModels,
      vehicleYears,
      selectedPartsList,
      commonParts,
      allParts,
      invoiceMessage,
      invoiceGenerated,
      disableInvoiceButton
    } = this.state;

    if (loading) {
      return (
        <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="page-wraper bg-gray py-100" style={{ textAlign: 'center' }}>
          <h4>Loading booking details...</h4>
        </div>
        </>
      );
    }

    if (error || !booking) {
      return (
        <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="page-wraper bg-gray py-100" style={{ textAlign: 'center' }}>
          <h4>{error || 'Booking not found'}</h4>
          <button className="btn btn-primary" onClick={this.goBack}>
            Back to Dashboard
          </button>
        </div>
        </>
      );
    }

    return (
      <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="mechanic-dashboard">
        <header>
          <h2>Booking Details</h2>
          <button className="logout-button" onClick={this.goBack}>
            ← Back to Dashboard
          </button>
        </header>

        <div className="modal-content-wrapper">
          <div className="modal-section">
            <div className="section-title">Basic Information</div>
          
            <p><strong>Booking ID:</strong> {booking.booking_id}</p>
            <p><strong>Date:</strong> {booking.date_of_service}</p>
            <p><strong>Time:</strong> {booking.time_of_service}</p>
            <p><strong>Status:</strong> <span className={`order-status ${booking.order_status === 'COMPLETED' || booking.order_status === 'CASH TAKEN' ? 'completed' : booking.order_status.includes('IN PROGRESS') || booking.order_status.includes('STARTED') ? 'in-progress' : CANCELLED_STATUSES.includes(booking.order_status) ? 'cancelled' : 'pending'}`}>{booking.order_status}</span></p>
            <p><strong>Vehicle:</strong> {booking.vehicle_name}</p>
            <p><strong>Service Type:</strong> {booking.service_type}</p>
          </div>

          <div className="modal-section">
            <div className="section-title">Vehicle Details</div>
            <label>Vehicle Make</label>
            <select value={booking.vehicle_make || ''} onChange={this.handleVehicleMakeChange}>
              <option value="">-- Select Make --</option>
              {(booking.vehicle_category?.toLowerCase() === 'car' ? vehicleMakesCar : vehicleMakesBike).map(mk => (
                <option key={mk.value} value={mk.value}>{mk.label}</option>
              ))}
            </select>
            <label>Vehicle Model</label>
            <select value={booking.vehicle_model || ''} onChange={this.handleVehicleModelChange} disabled={!booking.vehicle_make}>
              <option value="">-- Select Model --</option>
              {(vehicleModels[booking.vehicle_make] || []).map(mo => (
                <option key={mo.value} value={mo.value}>{mo.label}</option>
              ))}
            </select>
            <label>Vehicle Year</label>
            <select value={booking.vehicle_year || ''} onChange={this.handleVehicleYearChange}>
              <option value="">-- Select Year --</option>
              {vehicleYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {!TERMINAL_STATUSES.includes(booking.order_status) && (
            <>
              <div className="modal-section">
                <div className="section-title">Customer Information</div>
                <p><strong>Name:</strong> {booking.first_name}</p>
                <p><strong>Address:</strong> {booking.address}</p>
                <button
                  className="map-link-button logout-button"
                  onClick={() => this.openGoogleMaps(booking.latitude, booking.longitude)}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                >
                {/* <a
                      className="map-link-button"
                      href={`https://www.google.com/maps/dir/?api=1&destination=${booking.latitude},${booking.longitude}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                    > */}
                  Open Customer Location on Maps
                {/* </a> */}
                </button>
              </div>

              {booking.order_status !== 'SERVICE STARTED' && (
                <div className="modal-section otp-section">
                  <div className="section-title">Start Service</div>
                  {!isOtpSent ? (
                    <button
                      className="logout-button full-width"
                      onClick={this.sendOtpToCustomer}
                      disabled={isSendingOtp}
                    >
                      {isSendingOtp ? '⏳ Sending OTP...' : '📱 Confirm Start Service'}
                    </button>
                  ) : (
                    <div>
                      <div className={`otp-message ${otpVerificationMessage.includes('sent') ? 'success' : 'info'}`}>
                        {otpVerificationMessage}
                      </div>
                      <div className="otp-input-wrapper">
                        <input
                          type="text"
                          placeholder="Enter OTP"
                          value={enteredOtp}
                          onChange={(e) => this.setState({ enteredOtp: e.target.value })}
                          maxLength="6"
                        />
                        <button className="logout-button" onClick={this.verifyOtp}>
                          ✅ Start Service
                        </button>
                      </div>
                      {isOtpVerified && (
                        <div className="otp-message success">Service Started Successfully!</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {workerDetails.length > 0 && (
                <div className="modal-section">
                  <div className="section-title">Assign Worker</div>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <select
                      id="worker-select"
                      value={selectedWorker.worker_number || ""}
                      onChange={(e) => {
                        const selectedOption = workerDetails.find(worker => worker.phone === e.target.value);
                        this.setState({
                          selectedWorker: selectedOption ? {
                            worker_number: selectedOption.phone,
                            worker_name: selectedOption.name
                          } : {}
                        });
                      }}
                      style={{ flex: 1 }}
                    >
                      <option value="" disabled>Select a worker</option>
                      {workerDetails.map((worker) => (
                        <option key={worker.phone} value={worker.phone}>{worker.name}</option>
                      ))}
                    </select>
                    <button
                      className="logout-button"
                      onClick={() => this.assignWorker(selectedWorker)}
                      disabled={!selectedWorker.worker_number}
                      style={{ minWidth: '120px' }}
                    >
                      Assign
                    </button>
                  </div>
                </div>
              )}

              <div className="modal-section">
                <div className="section-title">Quick Actions</div>
                <div className="action-buttons-grid">
                  <button className="logout-button" onClick={this.callCustomer}>
                    📞 Call Customer
                  </button>
                  <button className="logout-button success" onClick={() => this.updateBookingStatus('IN PROGRESS')}>
                    ✅ Going to Customer
                  </button>
                  <button className="logout-button" onClick={() => this.updateBookingStatus('NO RESPONSE')}>
                    ❌ No Response
                  </button>
                  <button className="logout-button" onClick={() => this.updateBookingStatus('MECHANIC REACHED')}>
                    🏁 Reached
                  </button>
                  {/* <button className="logout-button" onClick={() => this.updateBookingStatus('IN PROGRESS - TOWED')}>
                    🚛 Towed
                  </button> */}
                </div>
              </div>

              <div className="modal-section notes-section">
                <div className="section-title">Add Notes</div>
                <div className="notes-input-wrapper">
                  <input
                    type="text"
                    name="mechanic_notes"
                    value={selectedBookingNotes}
                    placeholder='Enter notes here...'
                    onChange={this.handleNotesChange}
                    disabled={submitNotesDisabled}
                  />
                  <button className="logout-button" onClick={this.submitNotesUpdate} disabled={submitNotesDisabled}>
                    Save
                  </button>
                </div>
                {notesSuccessMessage && <div className="notes-success">{notesSuccessMessage}</div>}
              </div>

              {/* Spare-parts vendor: Call + Book Porter. (Parts Used below stays disabled — invoicing is on the WhatsApp LLM flow.) */}
              {nearbyVendors && nearbyVendors.length > 0 && (
                <div className="modal-section vendor-select">
                  <div className="section-title">Spare Parts Vendor</div>
                  <select
                    value={selectedVendor ? selectedVendor.phone : ''}
                    onChange={(e) => {
                      const selectedPhone = e.target.value;
                      const vendor = nearbyVendors.find((v) => v.phone === selectedPhone);
                      this.setState({ selectedVendor: vendor });
                    }}
                  >
                    <option value="">Select a vendor</option>
                    {nearbyVendors.map((vendor) => (
                      <option key={vendor.phone} value={vendor.phone}>
                        {vendor.name} - {vendor.distance.toFixed(2)} km away
                      </option>
                    ))}
                  </select>
                  {selectedVendor && (
                    <div className="vendor-actions">
                      <button className="logout-button" onClick={this.callVendor}>
                        📞 Call {selectedVendor.name}
                      </button>
                      <button className="logout-button" onClick={this.bookPorter} disabled={isBookingPorter}>
                        {isBookingPorter ? '⏳ Booking...' : '🚚 Book Porter'}
                      </button>
                    </div>
                  )}
                  {porterBookingMessage && (
                    <div className={`message ${porterBookingMessage.includes('successfully') ? 'success' : 'info'}`}>
                      {porterBookingMessage}
                    </div>
                  )}
                </div>
              )}

              <div className="modal-section photo-upload-section">
                <div className="section-title">Upload Photos</div>
                <div className="upload-button-wrapper">
                  <input type="file" multiple onChange={this.handlePhotoUpload} />
                  <div className="upload-button-label">
                    📷 Tap to Select Photos
                  </div>
                </div>
                {selectedPhotos.length > 0 && (
                  <div className="selected-photos-list">
                    <h5>Selected Photos ({selectedPhotos.length}):</h5>
                    <ul>
                      {selectedPhotos.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button className="logout-button full-width" onClick={this.uploadPhotos} disabled={selectedPhotos.length === 0}>
                  {selectedPhotos.length > 0 ? `📤 Upload ${selectedPhotos.length} Photo(s)` : 'Select Photos First'}
                </button>
                {uploadSuccessMessage && (
                  <div className={`message ${uploadSuccessMessage.includes('successfully') ? 'success' : 'error'}`}>
                    {uploadSuccessMessage}
                  </div>
                )}
              </div>

              {/* Parts Selection Section — DISABLED: invoicing moved to the WhatsApp LLM flow. Reintroduce by removing the `false && (` wrapper and its closing `)}`. */}
              {false && (
              <div className="modal-section">
                <div className="section-title">Parts Used</div>
                <div style={{ marginBottom: '1rem' }}>
                  <label>Common Parts</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {commonParts && commonParts.map((part) => (
                      <button
                        key={part.part_id}
                        className="logout-button"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                        onClick={() => this.handleClickPart(part)}
                      >
                        {part.part_name}
                      </button>
                    ))}
                    <button
                      className="logout-button secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      onClick={this.handleAddOtherPart}
                    >
                      + Add Other
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label>All Parts</label>
                  <select onChange={this.handleSelectPart} style={{ marginTop: '0.5rem' }}>
                    <option value="">Select a part</option>
                    {allParts && allParts.map((part) => (
                      <option
                        key={part.part_id}
                        data-part-id={part.part_id}
                        data-part-name={part.part_name}
                        value={part.part_name}
                      >
                        {part.part_name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedPartsList.length > 0 && (
                  <div>
                    <label>Selected Parts</label>
                    <div className="table-container" style={{ marginTop: '0.5rem' }}>
                      <table className="part-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Part Name</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Qty</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Price</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPartsList.map((selectedPart, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '0.75rem' }}>
                                <input
                                  type="text"
                                  placeholder="Part Name"
                                  value={selectedPart.partName}
                                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '8px' }}
                                  onChange={(e) => this.handleOtherPartNameChange(index, e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <input
                                  type="number"
                                  placeholder="Qty"
                                  value={selectedPart.quantity || 1}
                                  min="1"
                                  style={{ width: '60px', padding: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '8px' }}
                                  onChange={(e) => this.handleQuantityChange(index, parseInt(e.target.value) || 1)}
                                />
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  value={selectedPart.salesPrice}
                                  style={{ width: '100px', padding: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '8px' }}
                                  onChange={(e) => this.handleSalesPriceChange(index, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === '-' && (!selectedPart.salesPrice || selectedPart.salesPrice.startsWith('-'))) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <button
                                  className="logout-button danger"
                                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                                  onClick={() => this.removePart(index)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 700 }}>
                            <td colSpan="2" style={{ padding: '0.75rem', textAlign: 'right' }}>Total:</td>
                            <td style={{ padding: '0.75rem', color: '#f42222' }}>
                              ₹{selectedPartsList.reduce((total, part) => total + (parseFloat(part.salesPrice || 0) * (part.quantity || 1)), 0).toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {invoiceMessage && (
                      <div className={`message ${invoiceMessage.includes('Error') ? 'error' : 'success'}`} style={{ marginTop: '1rem' }}>
                        {invoiceMessage}
                      </div>
                    )}
                    <button
                      className={`logout-button success ${disableInvoiceButton || invoiceGenerated ? 'disabled-button' : ''}`}
                      style={{ marginTop: '1rem', width: '100%' }}
                      onClick={this.generateInvoice}
                      disabled={disableInvoiceButton || invoiceGenerated}
                    >
                      {invoiceGenerated ? '✓ Invoice Generated' : '💰 Generate Invoice'}
                    </button>
                  </div>
                )}
              </div>
              )}
            </>
          )}

          {(booking.order_status === 'COMPLETED' || booking.order_status === 'CASH TAKEN') && (
            <div className="modal-section">
              <div className="section-title">Completed Details</div>
              {booking.charges_desc && (
                <div>
                  <p><strong>Charges:</strong></p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0' }}>
                    {booking.charges_desc.split(',').map((item, index) => (
                      <li key={index} style={{ padding: '0.5rem', background: '#f8f9fa', marginBottom: '0.5rem', borderRadius: '8px' }}>
                        <strong>{item}:</strong> Rs. {(parseFloat(booking.amount_details.split(',')[index])).toFixed(1)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p><strong>Completed On:</strong> {this.formatDate(booking.completed_on)}</p>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }
}

export default withRouter(MechanicBookingDetails);

